import { API_CONFIG, getPangoHeaders, getPangoUrl } from '../config/api';
import { Order, OrdersResponse, OrderDetailsResponse, OrderStats } from '../types/order';

class OrderApiService {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    this.baseUrl = API_CONFIG.PANGO.BASE_URL;
    this.apiKey = API_CONFIG.PANGO.API_KEY;
    
    if (!this.baseUrl || !this.apiKey) {
      throw new Error('Order API configuration is incomplete');
    }
  }

  /**
   * Get orders for a specific outlet
   */
  async getOrders(
    outletId: string, 
    options: {
      page?: number;
      limit?: number;
      status?: string;
      startDate?: string;
      endDate?: string;
      sort_by?: string;
      sort_order?: string;
    } = {}
  ): Promise<OrdersResponse> {
    try {
      const {
        page = 1,
        limit = 20,
        status,
        startDate,
        endDate,
        sort_by = 'created_at',
        sort_order = 'desc'
      } = options;

      // Build query parameters according to API spec
      const queryParams = new URLSearchParams({
        sort_by,
        sort_order,
        limit: limit.toString(),
        offset: ((page - 1) * limit).toString(),
        ...(status && { status }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate })
      });

      // Replace placeholder in endpoint
      const endpoint = API_CONFIG.PANGO.ENDPOINTS.ORDERS.GET_ORDERS
        .replace('{outletId}', outletId);
      
      const url = `${getPangoUrl(endpoint)}?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getPangoHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `HTTP error! status: ${response.status}`
        );
      }

      const data: OrdersResponse = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get order details by ID
   */
  async getOrderDetails(outletId: string, orderId: string): Promise<OrderDetailsResponse> {
    try {
      // Use the new detail endpoint
      const endpoint = `/v1/ai/smart-order/order/${orderId}`;
      
      const url = getPangoUrl(endpoint);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: getPangoHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || 
          `HTTP error! status: ${response.status}`
        );
      }

      const data: OrderDetailsResponse = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Confirm an order (PENDING/PROPOSED -> CONFIRMED)
   */
  async confirmOrder(orderId: string, notes?: string): Promise<{ success: boolean; data?: { confirmed_order: Order }; message?: string }> {
    try {
      const response = await fetch(`${getPangoUrl(`/v1/ai/smart-order/confirm/${orderId}`)}`, {
        method: 'POST',
        headers: {
          ...getPangoHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          notes: notes || ''
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get order statistics for dashboard
   */
  async getOrderStats(outletId: string): Promise<OrderStats> {
    try {
      // Get orders with summary data
      const response = await this.getOrders(outletId, {
        page: 1,
        limit: 100 // Get more orders for better stats
      });

      if (!response.success || !response.data) {
        throw new Error('Failed to fetch order data');
      }

      const { summary } = response.data;
      
      // Use summary data from API
      if (summary) {
        const totalAmount = parseFloat(summary.total_amount);
        const averageOrderValue = summary.total_orders > 0 ? totalAmount / summary.total_orders : 0;

        // Calculate trend (simple implementation - can be enhanced)
        const trendValue = 0; // TODO: Implement proper trend calculation

        return {
          total_orders: summary.total_orders,
          pending_orders: summary.status_breakdown.PENDING,
          confirmed_orders: summary.status_breakdown.CONFIRMED,
          processing_orders: summary.status_breakdown.SHIPPING,
          completed_orders: summary.status_breakdown.DELIVERED,
          cancelled_orders: summary.status_breakdown.CANCELLED,
          total_amount: totalAmount,
          average_order_value: averageOrderValue,
          orders_trend: {
            value: Math.abs(trendValue),
            is_positive: trendValue >= 0
          }
        };
      }

      // Fallback to calculating from orders if no summary
      const { orders } = response.data;
      const totalOrders = orders.length;
      const pendingOrders = orders.filter(order => order.order_status === 'PENDING').length;
      const confirmedOrders = orders.filter(order => order.order_status === 'CONFIRMED').length;
      const processingOrders = orders.filter(order => order.order_status === 'SHIPPING').length;
      const completedOrders = orders.filter(order => order.order_status === 'DELIVERED').length;
      const cancelledOrders = orders.filter(order => order.order_status === 'CANCELLED').length;
      
      const totalAmount = orders.reduce((sum, order) => sum + parseFloat(order.final_amount), 0);
      const averageOrderValue = totalOrders > 0 ? totalAmount / totalOrders : 0;

      return {
        total_orders: totalOrders,
        pending_orders: pendingOrders,
        confirmed_orders: confirmedOrders,
        processing_orders: processingOrders,
        completed_orders: completedOrders,
        cancelled_orders: cancelledOrders,
        total_amount: totalAmount,
        average_order_value: averageOrderValue,
        orders_trend: {
          value: 0,
          is_positive: true
        }
      };
    } catch (error) {
      throw error;
    }
  }
}

// Create and export singleton instance
export const orderApi = new OrderApiService();
export default orderApi;
